import type { ConflictResponse } from "./handlers/models/conflictResponse";

export const defaultErrorMessage = "An error has occurred. Please try again.";

export enum BadRequestReason {
  NotSpecified = 0,
  RequestError = 1,
  Required = 2,
  InvalidValue = 3,
  AccountSyncError = 4,
  UserSyncError = 5,
  LimitExceeded = 6,
}

export enum ForbiddenReason {
  Denied = 0,
  UpgradeRequired = 1,
  NotConnected = 2,
  SubscriptionInactive = 3,
}

export class BaseResponse {
  constructor(apiResponse: unknown) {
    const body = (apiResponse ?? {}) as {
      success?: boolean;
      message?: string;
      needsToRelogin?: boolean;
      data?: unknown;
      forcedUpgrade?: unknown;
    };
    this.success = body.success ?? false;
    this.message = body.message ?? "";
    this.needsToRelogin = body.needsToRelogin ?? false;
    this.data = body.data;
    this.forcedUpgrade = body.forcedUpgrade;
  }

  success: boolean;
  message: string;
  needsToRelogin: boolean;
  data: unknown;
  forcedUpgrade: unknown;
}

export class ExceptionResponse extends BaseResponse {
  ExceptionMessage?: string;
  ExceptionType?: string;
  StackTrace?: string;
  Message?: string;
}

class BadRequestErrorInfo<TRequest = Record<string, unknown>> {
  constructor(data: unknown) {
    const info = (data ?? {}) as {
      fieldKey?: string | null;
      errorCode?: BadRequestReason;
      message?: string;
    };
    this.fieldKey = (info.fieldKey ?? null) as (keyof TRequest & string) | null;
    this.reason = info.errorCode ?? BadRequestReason.NotSpecified;
    this.message = info.message ?? "";
  }

  fieldKey: (keyof TRequest & string) | null;
  reason: BadRequestReason;
  message: string;
}

export class BadRequestError<TRequest = Record<string, unknown>> extends Error {
  constructor(data: unknown) {
    super();
    const body = (data ?? {}) as {
      errors?: unknown[];
      message?: string;
      extraInfo?: string;
    };
    this.errors =
      body.errors?.map((error) => new BadRequestErrorInfo<TRequest>(error)) ??
      [];
    this.message = body.message ?? "";
    this.extraInfo = body.extraInfo ?? "";
  }

  errors: BadRequestErrorInfo<TRequest>[];
  override message: string;
  extraInfo: string;
}

/**
 * Narrows an `unknown` to `BadRequestError` with the class's default type
 * parameter. Prefer this over a bare `instanceof BadRequestError` check when the
 * narrowed value is passed onward: `instanceof` on a generic class narrows to
 * `BadRequestError<any>`, which trips `@typescript-eslint/no-unsafe-argument` at
 * consumer call sites. The predicate pins the default generic instead.
 */
export function isBadRequestError(value: unknown): value is BadRequestError {
  return value instanceof BadRequestError;
}

export class ConflictError extends Error {
  constructor(response: ConflictResponse) {
    super(response.message ?? undefined);
    this.response = response;
  }

  response: ConflictResponse;
}

export class TooManyRequestsError extends Error {
  constructor(response: BaseResponse, message?: string) {
    super(message);
    this.response = response;
  }

  response: BaseResponse;
}

export class ServiceUnavailableError extends Error {
  constructor(
    response: BaseResponse,
    message?: string,
    retryAfterSeconds?: number | null,
  ) {
    super(message);
    this.response = response;
    this.retryAfterSeconds = retryAfterSeconds ?? null;
  }

  response: BaseResponse;
  retryAfterSeconds: number | null;
}

export class NotFoundError extends Error {
  constructor(response: BaseResponse = new BaseResponse({}), message?: string) {
    super(message);
    this.response = response;
  }

  response: BaseResponse;
}

export class UpgradeRequiredError extends Error {
  constructor(data: unknown) {
    const body = (data ?? {}) as {
      message?: string;
      currentPackage?: string;
      requiredPackage?: string;
      userCanUpgrade?: boolean;
    };
    super(body.message);
    this.currentPackage = body.currentPackage ?? "";
    this.requiredPackage = body.requiredPackage ?? "";
    this.userCanUpgrade = body.userCanUpgrade ?? false;
  }

  currentPackage: string;
  requiredPackage: string;
  userCanUpgrade: boolean;
}

export class NetworkError extends Error {
  constructor(response: BaseResponse | DOMException, message?: string) {
    super(message);
    this.response = response as BaseResponse;
    if (response instanceof DOMException) {
      this.domException = response;
    }
  }

  response: BaseResponse;
  domException?: DOMException;
}

export class APIError extends Error {
  /**
   * @param response The api response
   * @param message Optional, error message to use. This will default to the message that appears in the api response
   */
  constructor(
    response: ExceptionResponse,
    message?: string,
    endpoint?: string,
  ) {
    const synthesizedMessage = message
      ? undefined
      : APIError.synthesizeReportingMessage(response, endpoint);
    super(message ?? synthesizedMessage);
    this.response = response;
    if (endpoint !== undefined) {
      this.endpoint = endpoint;
    }
    this.hasSynthesizedMessage = synthesizedMessage !== undefined;
  }

  private static synthesizeReportingMessage(
    response: ExceptionResponse | undefined,
    endpoint: string | undefined,
  ): string {
    const endpointPath = endpoint ? endpoint.split("?")[0] : "unknown endpoint";
    if (response?.needsToRelogin) {
      return `APIError: needsToRelogin from ${endpointPath}`;
    }
    if (response?.success === false) {
      return `APIError: success=false from ${endpointPath}`;
    }
    return `APIError from ${endpointPath}`;
  }

  get errorMessage(): string {
    if (
      !this.hasSynthesizedMessage &&
      this.message &&
      this.message.length > 0
    ) {
      return this.message;
    }
    if (this.response) {
      if (this.response.message && this.response.message.length > 0) {
        return this.response.message;
      }
      const data = this.response.data;
      if (data && typeof data === "object") {
        const message = (data as { message?: unknown }).message;
        if (typeof message === "string" && message.length > 0) {
          return message;
        }
        const formMessage = (data as { formMessage?: unknown }).formMessage;
        if (typeof formMessage === "string" && formMessage.length > 0) {
          return formMessage;
        }
      }
    }

    return defaultErrorMessage;
  }

  response: ExceptionResponse;
  endpoint?: string;
  hasSynthesizedMessage: boolean;
}

export class PreviewActionAPIError extends APIError {
  constructor() {
    super(new BaseResponse({}), "Cannot commit actions inside of a preview.");
  }
}

export class DetailedError extends Error {
  response!: string;
}

export class NoAccessError extends Error {
  constructor(data: unknown) {
    super((data as { message?: string } | null)?.message);
    this.response = data as BaseResponse;
    this.reason =
      (data as { reason?: ForbiddenReason } | null)?.reason ??
      ForbiddenReason.Denied;
  }

  response: BaseResponse;
  reason: ForbiddenReason;
}
